import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {MenuItem} from 'primeng/api';
import {PageComponent} from 'features/core/shared/mvs-page/page.component';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';

/**
 * Shell page of the provision dashboard (route `cp/dashboard`, hash routing: `/#/cp/dashboard`).
 *
 * Built 1:1 following the bm original `features/feature-bm/bm/page/
 * bm-billing-dashboard-overview-page/bm-billing-dashboard-overview.page.ts` (PAT §J.2):
 * the page declares only `navigationItems` (action = child route path), keeps the breadcrumb
 * and active navigation in sync with the router events, and renders exclusively
 * `<router-outlet>` in its template. The business content is delivered by the child components
 * (CpProvisionDashboardStartComponent / CpProvisionRunsComponent /
 * CpProvisionRunDetailComponent / CpProvisionAgentsComponent).
 *
 * Job indicator (04-frontend.md §4, PAT §J.2): NOT built here. `<jb-spring-batch-indicator>`
 * sits globally in the top bar; here it is only armed via JbSpringBatchIndicatorService
 * (`jobNamePattern: 'cp%'`) and deactivated again in ngOnDestroy. Business components
 * subscribe to `status$` and reload on the running -> not-running transition.
 */
@Component({
    selector: 'cp-dashboard-page',
    templateUrl: './cp-dashboard.page.html',
    styleUrls: ['./cp-dashboard.page.scss'],
    standalone: false
})
export class CpDashboardPage extends PageComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * Child route path of the start page. The start page is the default child (`path: ''`) of
     * route `cp/dashboard` (04-frontend.md §2) — so its action is empty and
     * navigation goes via the relative path '.'.
     */
    static readonly START_ACTION: string = '';

    defaultLabel: string = 'Provisions-Dashboard';

    routerSubscription: Subscription;

    /**
     * Navigation entries of the shell. `action` matches exactly the child route path from
     * CpSubRoutes(); labels are German business terms ("Provision…", never "Cp").
     */
    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: CpDashboardPage.START_ACTION,
            tooltip: 'Provisionsübersicht mit Kennzahlen je Provisionstyp',
            icon: 'fa-solid fa-gauge-high',
            toggleable: false,
            default: true
        },
        {
            label: 'Provisionsläufe',
            action: 'runs',
            tooltip: 'Alle Provisionsläufe',
            icon: 'fa-solid fa-list-check',
            toggleable: false,
            default: false
        },
        {
            label: 'Abrechnungskonten',
            action: 'agents',
            tooltip: 'Abrechnungskonten der Berater mit Provisionskonto, Rücklage und Salden',
            icon: 'fa-solid fa-user-tie',
            toggleable: false,
            default: false
        },
        {
            label: 'Simulation',
            action: 'simulation',
            tooltip: 'Provision für einen Vertrag durchrechnen und im Detail sehen, wie gerechnet wurde '
                + '— ohne zu buchen',
            icon: 'fa-regular fa-flask',
            toggleable: false,
            default: false
        }
    ];

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService,
        protected router: Router,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        this.defaultLabel = 'Provisions-Dashboard';
        super.ngOnInit();
        this.initComponent();
    }

    /**
     * Initialize component.
     */
    initComponent(): void {
        this.updateBreadcrumbFromRoute();
        this.updateActiveNavigationFromRoute();

        this.routerSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.updateBreadcrumbFromRoute();
                this.updateActiveNavigationFromRoute();
            });

        // 04-frontend.md §4: activate the globally mounted spring batch indicator for cp jobs
        // (cp_provision_calculation, cp_provision_balance, ...). The indicator component itself
        // lives in the topbar and is never rebuilt here.
        this.springBatchIndicatorService.activate({
            jobNamePattern: 'cp%',
            pollIntervalMs: 60000,
            showStopButton: true,
            label: 'Provisions-Jobs'
        });

        this.initialized = true;
    }

    /**
     * Override base class subscribeToParams to navigate to child routes based on the navItem
     * query param (`/#/cp/dashboard?navItem=runs`).
     */
    override subscribeToParams(): void {
        this.paramSubscription = this.route.queryParams.subscribe(params => {
            const navItem = params['navItem'];

            if (!this.navigationItems?.length) {
                return;
            }

            if (navItem !== undefined && navItem !== null) {
                const foundItem = this.navigationItems.find(item => item.action === navItem);
                if (foundItem) {
                    this.activeNavigationItem = foundItem;
                    this.navigateToNavigationItem(foundItem);
                }
                return;
            }

            const childPath = this.route.snapshot.firstChild?.url[0]?.path;
            if (!childPath) {
                const defaultItem = this.navigationItems.find(item => item.default === true);
                if (defaultItem) {
                    this.activeNavigationItem = defaultItem;
                }
            }
        });
    }

    /**
     * Override postNavigationSelection to navigate to the selected child route.
     */
    override postNavigationSelection(): void {
        if (this.activeNavigationItem) {
            this.navigateToNavigationItem(this.activeNavigationItem);
        }
    }

    /**
     * Navigate to a navigation item's child route. The start page is the default child
     * (`path: ''`), so it is reached via the relative path '.' rather than an empty segment.
     */
    private navigateToNavigationItem(item: NavigationItem): void {
        const commands = item.action ? [item.action] : ['.'];
        this.router.navigate(commands, {relativeTo: this.route});
    }

    /**
     * Update the active navigation item from the currently activated child route.
     * `run/:provisionRunId` is a detail view of the runs list and therefore keeps the
     * "Provisionsläufe" item active.
     */
    private updateActiveNavigationFromRoute(): void {
        if (!this.navigationItems?.length) {
            return;
        }

        const childPath = this.route.snapshot.firstChild?.url[0]?.path;
        const navAction = childPath === 'run' ? 'runs' : childPath;

        if (navAction) {
            const foundItem = this.navigationItems.find(item => item.action === navAction);
            if (foundItem && this.activeNavigationItem?.action !== foundItem.action) {
                this.activeNavigationItem = foundItem;
            }
            return;
        }

        const defaultItem = this.navigationItems.find(item => item.default === true);
        if (defaultItem && this.activeNavigationItem?.action !== defaultItem.action) {
            this.activeNavigationItem = defaultItem;
        }
    }

    /**
     * Breadcrumb trail per child route — German labels, hash routing links.
     */
    private updateBreadcrumbFromRoute(): void {
        const childPath = this.route.snapshot.firstChild?.url[0]?.path;
        const provisionRunId = this.route.snapshot.firstChild?.paramMap.get('provisionRunId');

        const breadcrumbs: MenuItem[] = [];

        if (!childPath) {
            breadcrumbs.push({label: 'Provisionsübersicht', routerLink: ['/cp/dashboard']});
        } else if (childPath === 'runs') {
            breadcrumbs.push({label: 'Provisionsläufe', routerLink: ['/cp/dashboard/runs']});
        } else if (childPath === 'run') {
            breadcrumbs.push({label: 'Provisionsläufe', routerLink: ['/cp/dashboard/runs']});
            if (provisionRunId) {
                breadcrumbs.push({label: `Provisionslauf ${provisionRunId}`});
            }
        } else if (childPath === 'agents') {
            breadcrumbs.push({label: 'Abrechnungskonten', routerLink: ['/cp/dashboard/agents']});
        } else if (childPath === 'simulation') {
            breadcrumbs.push({label: 'Simulation', routerLink: ['/cp/dashboard/simulation']});
        }

        this.setBreadcrumbItems(breadcrumbs);
    }

    isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

    /**
     * Process changes within binding.
     *
     * Chains into the base implementation first — every lifecycle override of this class calls its
     * super, so that a later addition to PageComponent cannot be silently swallowed here.
     */
    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     *
     * `super.ngOnDestroy()` is MANDATORY. The base implementation
     * (`features/core/shared/mvs-page/page.component.ts`, ngOnDestroy) does two things: it detaches
     * `paramSubscription` AND it calls `coreService.getPageService().notifyPageDestroyed(this)`.
     * Re-implementing only the unsubscribe by hand drops the notification, so the PageService keeps
     * pointing at a page that no longer exists.
     *
     * DEVIATION from the bm reference `features/feature-bm/bm/page/
     * bm-billing-dashboard-overview-page/bm-billing-dashboard-overview.page.ts` (ngOnDestroy):
     * that file unsubscribes `paramSubscription` by hand and never chains into super. That is a
     * defect of the reference, not a pattern to copy — the notification is part of the base
     * contract, so this page calls super and leaves the paramSubscription to it.
     */
    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe();
        this.springBatchIndicatorService.deactivate();

        super.ngOnDestroy();
    }

}
